import React, { useEffect, useRef, useState } from 'react';

import { type Feature } from './featureFlags';
import { featureFlags } from './featureFlags';
import { useFeatureFlagsContext } from './useFeatureFlags';

import './FeatureFlagsPanel.sass';

const LOCAL_STORAGE_VISIBLE_KEY = 'featurePanelVisible';
const LOCAL_STORAGE_MINIMIZED_KEY = 'featurePanelMinimized';
const LOCAL_STORAGE_POSITION_KEY = 'featurePanelPosition';

export const FeatureFlagPanel: React.FC = () => {
    const [isVisible, setIsVisible] = useState(() => {
        try {
            return localStorage.getItem(LOCAL_STORAGE_VISIBLE_KEY) === 'true';
        } catch {
            return false;
        }
    });

    const [isMinimized, setIsMinimized] = useState(() => {
        try {
            return localStorage.getItem(LOCAL_STORAGE_MINIMIZED_KEY) === 'true';
        } catch {
            return false;
        }
    });

    const [position, setPosition] = useState(() => {
        try {
            const storedPosition = localStorage.getItem(LOCAL_STORAGE_POSITION_KEY);
            if (storedPosition) {
                return JSON.parse(storedPosition);
            }
        } catch {
            /* ignore */
        }
        // Default position if none is stored
        return { x: window.innerWidth - 380, y: 0 };
    });

    const dragState = useRef({
        isDragging: false,
        didDrag: false,
        startX: 0,
        startY: 0,
        offsetX: 0,
        offsetY: 0,
    });

    const { flags, toggleFeature } = useFeatureFlagsContext();

    useEffect(() => {
        const openInDefaultPosition = () => {
            setPosition({ x: window.innerWidth - 380, y: 0 });
            setIsVisible(true);
        };

        (window as any).fíčury = openInDefaultPosition;
        (window as any).features = openInDefaultPosition;

        return () => {
            delete (window as any).fíčury;
            delete (window as any).features;
        };
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(LOCAL_STORAGE_VISIBLE_KEY, String(isVisible));
        } catch (e) {
            console.error('Failed to save feature panel visibility state', e);
        }
    }, [isVisible]);

    useEffect(() => {
        try {
            localStorage.setItem(LOCAL_STORAGE_MINIMIZED_KEY, String(isMinimized));
        } catch (e) {
            console.error('Failed to save feature panel minimized state', e);
        }
    }, [isMinimized]);

    useEffect(() => {
        try {
            localStorage.setItem(LOCAL_STORAGE_POSITION_KEY, JSON.stringify(position));
        } catch (e) {
            console.error('Failed to save feature panel position state', e);
        }
    }, [position]);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Don't drag on minimize button click or other controls
        if ((e.target as HTMLElement).closest('.minimize-button, .panel-controls')) {
            return;
        }

        document.body.style.userSelect = 'none'; // zamezuje vyber textu pri dragovani

        dragState.current = {
            isDragging: true,
            didDrag: false,
            startX: e.clientX,
            startY: e.clientY,
            offsetX: e.clientX - position.x,
            offsetY: e.clientY - position.y,
        };

        const handleMouseMove = (me: MouseEvent) => {
            if (!dragState.current.isDragging) {
                return;
            }

            const dx = me.clientX - dragState.current.startX;
            const dy = me.clientY - dragState.current.startY;
            if (!dragState.current.didDrag && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                dragState.current.didDrag = true;
            }

            setPosition({
                x: me.clientX - dragState.current.offsetX,
                y: me.clientY - dragState.current.offsetY,
            });
        };

        const handleMouseUp = () => {
            document.body.style.userSelect = ''; // obnovime vyber textu
            if (!dragState.current.didDrag) {
                setIsMinimized((v) => !v);
            }
            dragState.current.isDragging = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    if (!isVisible) {
        return null;
    }

    const { isDragging } = dragState.current;

    return (
        <div
            className={`feature-flag-panel ${isMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{ left: `${position.x}px`, top: `${position.y}px` }}
        >
            <div className="feature-flag-panel-header" onMouseDown={handleMouseDown}>
                <div className="header-title">
                    <span className="minimize-button" onClick={() => setIsMinimized((v) => !v)}>
                        {isMinimized ? '▹' : '▿'}
                    </span>
                    <h2>Feature Flags</h2>
                </div>
                <div className="panel-controls">
                    <button
                        className="close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsVisible(false);
                        }}
                    >
                        &times;
                    </button>
                </div>
            </div>
            <div className="feature-flag-panel-content">
                {Object.entries(featureFlags).map(([key, { description }]) => (
                    <div key={key} className="feature-flag-item">
                        <label>
                            <input
                                checked={flags[key as Feature]}
                                type="checkbox"
                                onChange={() => toggleFeature(key as Feature)}
                            />
                            <div className="feature-details">
                                <span className="feature-name">{key}</span>
                                <span className="feature-description">{description}</span>
                            </div>
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
};
